import { BaseComponent, define, prefersReducedMotion } from 'core';

/**
 * Hero "hikaye" slaytı (bkz. snippets/story-slide.liquid).
 *
 * Hikaye slayt AKTİF olduğunda, yani kayarak gelip yerine OTURDUĞUNDA başlar
 * (görünürlük >= %95). Ölçüm sayfaya göre değil karuselin kaydırma alanına
 * (track) göre: sayfa biraz aşağı kaydırılmış olsa da slayt aktifse oynar.
 * Kareler daha erken, slayt %60 görününce yüklenmeye başlar. Slayt %60'ın
 * altına inince (başka slayta geçildi) hikaye başa sarar; bir sonraki
 * gelişte başlık girişiyle birlikte baştan anlatılır.
 *
 * Giriş BEKLERKEN (`data-await-intro`) başlık, logolar ve buton gizli: slayt
 * kayarak gelirken yalnızca fotoğraf görünür, yazılar yerine oturunca gelir.
 * Aksi hâlde yazı kayarken görünüp oturunca kaybolup yeniden geliyordu.
 *
 * Fare: slayt kendi içinde bir gösteri, bu yüzden fare üstündeyken de döner
 * (karusel o sırada bu slaytta bekliyor). Son kareye gelinmişse ve fare hâlâ
 * üstündeyse hikaye başa döner. Blok ayarı `pause_on_hover` açıksa eski
 * davranış: fare üstündeyken durur.
 * Her durumda durur: klavye odağı içerideyken ve sekme arka plandayken.
 * Azaltılmış hareket isteyen ziyaretçide hiç başlamaz; slayt durağan kalır.
 *
 * Sonraki karelerin görselleri `data-story-src/srcset`te bekliyor ve slayt
 * İLK kez görünür olduğunda yükleniyor — sayfa açılışında yalnızca ilk kare iner.
 */
const HOLD_MS = 1500;

export class StorySlide extends BaseComponent {
  connectedCallback() {
    super.connectedCallback();
    this._frames = Array.from(this.querySelectorAll('[data-story-frame]'));
    this._beats = Array.from(this.querySelectorAll('[data-story-beat]'));
    // Başlık katmanları: ilki ana başlık, diğerleri `data-beat="<kare>"` ile
    // kendi karesine bağlı. Kendi başlığı olmayan karede ana başlık görünür.
    this._heads = Array.from(this.querySelectorAll('[data-story-head]'));
    this._mainHead = this._heads.find((h) => !h.hasAttribute('data-beat'));
    this._beatMs = parseInt(this.dataset.beatMs || '', 10) || 3000;
    this._pauseOnHover = this.hasAttribute('data-pause-on-hover');

    if (this._frames.length < 2 || prefersReducedMotion()) return;
    if (!('IntersectionObserver' in window)) return;

    // Slayt sayfa açılırken ZATEN ekrandaysa (hero'nun ilk slaytı) başlık
    // HTML'le birlikte boyanmış demektir; ilk oynatmada girişi tekrar oynatmak
    // metni bir an gizleyip geri getirir. O ilk seferde başlık girişi atlanır.
    // "Ekranda" = AKTİF slayt: geniş ekranda (peek) komşu slaytın kenarı da
    // ekranda görünüyor, onu saymamak için yatayda track'e tam oturmuş olmalı.
    const r = this.getBoundingClientRect();
    const track = this.closest('[data-carousel-track]');
    const tr = track ? track.getBoundingClientRect() : { left: 0, right: window.innerWidth };
    const onScreen = r.top < window.innerHeight && r.bottom > 0;
    const settled = r.left >= tr.left - 2 && r.right <= tr.right + 2;
    this._skipIntro = onScreen && settled;

    this.setAttribute('data-animated', '');
    if (!this._skipIntro) this.setAttribute('data-await-intro', '');
    this.style.setProperty('--story-beat-ms', `${this._beatMs}ms`);

    this._index = 0;
    this._timer = null;
    this._visible = false;
    this._hovered = false;
    this._loaded = false;

    this._observer = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        if (ratio >= 0.6) this._loadFrames();
        if (ratio >= 0.95 && !this._visible) {
          this._visible = true;
          this._restart();
        } else if (ratio < 0.6 && this._visible) {
          this._visible = false;
          this._halt();
          this._reset();
          this.setAttribute('data-await-intro', '');
        }
      },
      { root: this.closest('[data-carousel-track]'), threshold: [0, 0.6, 0.95] }
    );
    this._observer.observe(this);

    this.on(this, 'mouseenter', () => {
      this._hovered = true;
      if (this._pauseOnHover) this._pause();
    });
    this.on(this, 'mouseleave', () => {
      this._hovered = false;
      this._resume();
    });
    this.on(this, 'focusin', () => this._pause());
    this.on(this, 'focusout', () => this._resume());
    this.on(document, 'visibilitychange', () => {
      if (document.hidden) this._pause();
      else this._resume();
    });
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    this._halt();
    super.disconnectedCallback();
  }

  _loadFrames() {
    if (this._loaded) return;
    this._loaded = true;
    // `data-story-*`: karuselin lazy yükleyicisi `data-src`i aradığı için
    // ayrı ad; yoksa kareler slayt görünmeden, sayfa açılışında iniyordu.
    this.querySelectorAll('img[data-story-src]').forEach((img) => {
      if (img.dataset.storySrcset) img.srcset = img.dataset.storySrcset;
      img.src = img.dataset.storySrc;
      delete img.dataset.storySrcset;
      delete img.dataset.storySrc;
    });
  }

  /** Başa sarar: yakınlaşma ve metin animasyonları da sıfırlanır. */
  _reset() {
    this._index = 0;
    this._frames.forEach((f) => f.removeAttribute('data-shown'));
    this.removeAttribute('data-intro');
    this.removeAttribute('data-ended');
    this.removeAttribute('data-still');
    // Animasyonu yeniden tetiklemek için nitelik kaldırıldıktan sonra bir
    // reflow okunmalı; yoksa tarayıcı iki değişikliği birleştiriyor.
    void this.offsetWidth;
    this._render();
  }

  _restart() {
    this._halt();
    this._reset();
    this.removeAttribute('data-await-intro');
    if (this._skipIntro) {
      // Açılışta zaten boyanmış ana başlık tekrar oynatılmasın; ilk kare
      // değişiminde bu nitelik kalkıyor ve başlıklar normal akışına dönüyor.
      this._skipIntro = false;
      this.setAttribute('data-still', '');
    } else {
      this.setAttribute('data-intro', '');
    }
    this._frames[0].setAttribute('data-shown', '');
    this._remaining = this._beatMs;
    this._resume();
  }

  _render() {
    const i = this._index;
    const toggle = (list) =>
      list.forEach((el, n) => {
        el.toggleAttribute('data-active', n === i);
        el.toggleAttribute('data-done', n < i);
      });
    toggle(this._frames);
    toggle(this._beats);
    const head = this._heads.find((h) => h.dataset.beat === String(i)) || this._mainHead;
    this._heads.forEach((h) => h.toggleAttribute('data-active', h === head));
  }

  _next() {
    this._timer = null;
    if (this.hasAttribute('data-ended')) {
      // Kapanış karesinden sonra: fare hâlâ üstündeyse (karusel bekliyor)
      // hikaye baştan döner; değilse karusel zaten sıradaki slayta geçecek.
      if (this._hovered && !this._pauseOnHover) this._restart();
      return;
    }
    if (this._index >= this._frames.length - 1) {
      // Son karede CTA ile kısa bekleme. Karuselin slayt süresi bu payı
      // içeriyor (hero-slider data-duration).
      this.setAttribute('data-ended', '');
      this._remaining = HOLD_MS;
      this._schedule();
      return;
    }
    this._index += 1;
    this.removeAttribute('data-still');
    this._frames[this._index].setAttribute('data-shown', '');
    this._render();
    this._remaining = this._beatMs;
    this._schedule();
  }

  _schedule() {
    this._startedAt = performance.now();
    this._timer = setTimeout(() => this._next(), this._remaining);
  }

  _pause() {
    this.setAttribute('data-paused', '');
    if (!this._timer) return;
    clearTimeout(this._timer);
    this._timer = null;
    this._remaining = Math.max(0, this._remaining - (performance.now() - this._startedAt));
  }

  _resume() {
    if (!this._visible || document.hidden) return;
    if (this._hovered && this._pauseOnHover) return;
    if (this.matches(':focus-within')) return;
    this.removeAttribute('data-paused');
    if (this._timer) return;
    this._schedule();
  }

  _halt() {
    clearTimeout(this._timer);
    this._timer = null;
  }
}

define('story-slide', StorySlide);
